import React, { useEffect, useState } from "react";
import { DELETE_ICON, SQUARE_PLUS_ICON } from "../../utils/aap-image-constant";
import { ROUTES } from "../../hooks/routes/routes-constant";
import Input from "../../components/common/input";
import { addQuestionNew, getPreAppointmentQuestionsById } from "../../hooks/services/api-services";
import { decryptAEStoJSON, toastEmitter } from "../../utils/utilities";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrum from "../../components/common/BreadCrum";
import { useTranslation } from "react-i18next";
import Button from "../../components/common/button";

const AddQuestions = () => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const { doctorId } = useParams();
    const { value } = useSelector((state) => state?.loggedUser);
    const [isLoading, setIsLoading] = useState(false);
    const userData = decryptAEStoJSON(value);
    const [isQuestionAdded, setIsQuestionAdded] = useState(false);

    const [formState, setFormState] = useState({
        id: 0,
        doctorId: doctorId || 0,
        clinicId: userData?.id || 0,
        languageType:i18n.language,
        appointmentQuestionList: [
            {
                id: 0,
                questionName: "",
                questionDescription: "",
                optionRequestList: [{ id: 0, description: "" }, { id: 0, description: "" },],
            },
        ],
    });

    useEffect(() => {
        if (doctorId && userData?.id) {
            setFormState((prevState) => ({
                ...prevState,
                doctorId: Number(doctorId),
                clinicId: userData?.id,
            }));
        }
    }, [doctorId, userData?.id]);

    useEffect(() => {
        if (doctorId) {
            fetchDoctorData(doctorId);
        }
    }, [doctorId,i18n.language]);

    const fetchDoctorData = async (id) => {
        try {
            const languageType=i18n.language;
            const response = await getPreAppointmentQuestionsById(id,languageType);
            const questions = response?.data?.data?.appointmentQuestionList || [];
            if (questions?.length > 0) {
                setIsQuestionAdded(true);
                const mappedQuestions = questions && questions?.map((q) => ({
                    id: q?.id || 0,
                    questionName: q?.questionName || "",
                    questionDescription: q?.questionDescription || "",
                    optionRequestList: q?.optionRequestList?.map((opt) => ({
                        id: opt?.id || 0,
                        description: opt?.description || "",
                    })) || [{ id: 0, description: "" }, { id: 0, description: "" },],
                }));

                setFormState((prev) => ({
                    ...prev,
                    id: response?.data?.data?.id || 0, // Ensure the 'id' is set from response data
                    doctorId: response?.data?.data?.doctorId || 0, // Ensure the 'doctorId' is set
                    clinicId: response?.data?.data?.clinicId || 0,
                    appointmentQuestionList: mappedQuestions,
                }));
            }
        } catch (error) {
        }
    };



    const handleAddQuestion = () => {
        const lastQuestion = formState?.appointmentQuestionList?.at(-1);
        if (lastQuestion?.questionName?.trim() === "") {
            toastEmitter("error", "Please fill out the current question before adding another.");
            return;
        }
        if (lastQuestion?.optionRequestList?.length < 2) {
            toastEmitter("error", "Each question must have at least two options before adding a new one.");
            return;
        }
        if (formState?.appointmentQuestionList?.length < 4) {
            setFormState((prevState) => ({
                ...prevState,
                appointmentQuestionList: [
                    ...prevState?.appointmentQuestionList,
                    {
                        id: 0,
                        questionName: "",
                        questionDescription: "",
                        optionRequestList: [{ id: 0, description: "" }, { id: 0, description: "" }],
                    },
                ],
            }));
        }
    };
    const handleAddOption = (qIndex) => {
        const question = formState?.appointmentQuestionList[qIndex];

        const hasEmpty = question?.optionRequestList.some(
            (option) => option?.description.trim() === ""
        );

        if (hasEmpty) {
            toastEmitter("error", "Please fill all existing options before adding a new one.");
            return;
        }

        if (question?.optionRequestList?.length >= 4) return;

        const updatedQuestions = formState?.appointmentQuestionList.map((q, index) => {
            if (index === qIndex) {
                return {
                    ...q,
                    optionRequestList: [
                        ...q.optionRequestList,
                        { id: 0, description: "" },
                    ],
                };
            }
            return q;
        });

        setFormState((prevState) => ({
            ...prevState,
            appointmentQuestionList: updatedQuestions,
        }));
    };

    const handleInputChange = (e, questionIndex, field) => {
        const updated = [...formState?.appointmentQuestionList];
        updated[questionIndex][field] = e.target.value;
        setFormState({ ...formState, appointmentQuestionList: updated });
    };

    const handleOptionChange = (e, questionIndex, optionIndex) => {
        const updated = [...formState?.appointmentQuestionList];
        updated[questionIndex].optionRequestList[optionIndex].description = e.target.value;
        setFormState({ ...formState, appointmentQuestionList: updated });
    };

    const handleDeleteOption = (qIndex, oIndex) => {
        const updatedQuestions = [...formState.appointmentQuestionList];
        if (updatedQuestions[qIndex].optionRequestList.length <= 1) {
            toastEmitter("error", "At least one option is required.");
            return;
        }
        updatedQuestions[qIndex].optionRequestList.splice(oIndex, 1);
        setFormState({ ...formState, appointmentQuestionList: updatedQuestions });
    };

    const handleDeleteQuestion = (qIndex) => {
        if (formState?.appointmentQuestionList?.length <= 1) {
            toastEmitter("error", "At least one question is required.");
            return;
        }
        const updated = formState?.appointmentQuestionList?.filter((_, index) => index !== qIndex);
        setFormState({ ...formState, appointmentQuestionList: updated });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formState?.appointmentQuestionList?.length < 2) {
            toastEmitter("error", "Please add at least two questions before submitting.");
            return;
        }
        const hasInvalidQuestion = formState?.appointmentQuestionList?.some((question, index) => {
            return question?.optionRequestList?.filter(opt => opt.description.trim() !== "").length < 2;
        });

        if (hasInvalidQuestion) {
            toastEmitter("error", "Each question must have at least two options before submitting.");
            return;
        }

        for (const q of formState?.appointmentQuestionList) {
            if (!q?.questionName?.trim()) {
                toastEmitter("error", "All questions must have a name.");
                return;
            }
            if (q.optionRequestList?.some((opt) => !opt?.description?.trim())) {
                toastEmitter("error", "All options must have a description.");
                return;
            }
        }

        setIsLoading(true);

        try {
            const response = await addQuestionNew(formState);
            if (response?.data?.status === 200) {
            setIsQuestionAdded(true);
            navigate(ROUTES.DOCTOR);
            } else {
                toastEmitter("error", response?.data?.message || "Something went wrong.");
            }
        } catch (error) {
            toastEmitter("error", "Server error. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };



    return (
        <div className="main_datatable mt-lg-3 mt-1">
            <BreadCrum
                firstData={t("Doctor Management")}
                iconshow1={true}
                secondData={isQuestionAdded ? "Edit Question" :t("Add Question")}
                onFirstDataClick={() => navigate(ROUTES.DOCTOR)}
            />

            <form onSubmit={handleSubmit}>

                <div className="d-flex justify-content-between">
                    <h6 className="mb-0">
                       {t("Question")} ({formState?.appointmentQuestionList?.length}/4)
                    </h6>
                    <span
                        onClick={handleAddQuestion}
                        style={{
                            cursor: formState?.appointmentQuestionList?.length >= 4 ? "not-allowed" : "pointer",
                            opacity: formState?.appointmentQuestionList?.length >= 4 ? 0.5 : 1,
                        }}
                    >
                        <img src={SQUARE_PLUS_ICON} alt="Add Question" />
                    </span>
                </div>
                {formState.appointmentQuestionList.map((question, qIndex) => (
                    <div key={question?.id}>
                        <div className="card p-3 border border-radius_input mb-3 mt-2 text-start">
                            {formState?.appointmentQuestionList?.length > 1 && (
                                <div className="ms-2 d-flex justify-content-end">
                                    <img
                                        src={DELETE_ICON}
                                        alt="Delete Question"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleDeleteQuestion(qIndex)}
                                    />
                                </div>
                            )}
                            <div className="row">
                                <div className="col-md-6 mb-2">
                                    <Input
                                        type="text"
                                        value={question?.questionName}
                                        labelName={t("Question")}
                                        placeholder="Enter question"
                                        lableClassName={`${i18n.language==="ar"?"d-flex align-items-start":"d-flex align-items-end"}`}
                                        onChange={(e) => handleInputChange(e, qIndex, "questionName")}
                                    />
                                </div>

                                <div className="row">
                                    <label className={`d-flex flex-wrap mb-2  ${i18n.language==="ar"?"align-items-end":"align-items-start"}`}>{t("Options")}:</label>
                                    <div className="d-flex flex-wrap align-items-start" >
                                        {question?.optionRequestList && question?.optionRequestList?.map((option, oIndex) => (
                                            <div
                                                key={oIndex}
                                                className="position-relative me-4 mb-2"
                                                style={{ width: "150px" }}
                                            >
                                                <Input
                                                    type="text"
                                                    showLabel={false}
                                                    value={option?.description}
                                                    placeholder={`Option ${oIndex + 1}`}
                                                    onChange={(e) =>
                                                        handleOptionChange(e, qIndex, oIndex)
                                                    }
                                                    showAsterisk={false}
                                                />
                                                <span
                                                    onClick={() => {
                                                        if (question?.optionRequestList?.length <= 1) return; // block click
                                                        handleDeleteOption(qIndex, oIndex);
                                                    }}
                                                    style={{
                                                        cursor: question?.optionRequestList?.length <= 1 ? "not-allowed" : "pointer",
                                                        opacity: question?.optionRequestList?.length <= 1 ? 0.5 : 1,
                                                    }}
                                                >
                                                    <img
                                                        src={DELETE_ICON}
                                                        className="editicon"
                                                        style={{
                                                            position: "absolute",
                                                            top: "12px",
                                                            right: "-20px",
                                                        }}
                                                        alt="Delete icon"
                                                    />
                                                </span>


                                            </div>
                                        ))}
                                        <div className="d-flex align-items-end mb-2" style={{ position: "relative" }}>
                                            <img
                                                src={SQUARE_PLUS_ICON}
                                                alt="Add Option"
                                                className={`${i18n.language === "ar" ? "me-2" : ""}`}
                                                onClick={() => handleAddOption(qIndex)}
                                                style={{
                                                    position: "relative",
                                                    cursor:
                                                        question?.optionRequestList?.length >= 4
                                                            ? "not-allowed"
                                                            : "pointer",
                                                    opacity:
                                                        question?.optionRequestList?.length >= 4 ? 0.5 : 1,
                                                    width: "20px",
                                                    height: "20px",
                                                    marginLeft: "10px",
                                                    top: "10px",

                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="d-flex justify-content-center">
                    <Button
                        type="button"
                        className={`px-4 px-lg-5 py-2 rounded border-0  me-2  ${i18n.language === "ar" ? "ms-3" : ""}`}
                        label="Cancel"
                        onClick={() => navigate(ROUTES.DOCTOR)}
                    />
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="px-4 px-lg-5 py-2 rounded border-0 submitbtn text-white"
                    >
                        {isLoading ? "Saving..." : isQuestionAdded ? "Update" :t("Save")}
                    </button>

                </div>
            </form>
        </div>
    );
};

export default AddQuestions;
